import { useEffect, useState } from "react";
import { HiOutlineChatBubbleLeftRight } from "react-icons/hi2";
import { useLocation } from "react-router-dom";
import showToast from "../../utils/toastNotifications";

const TIDIO_SCRIPT_ID = "coursepivot-tidio-script";
const TIDIO_SCRIPT_SRC =
  "https://code.tidio.co/qvlvy9h50jqcfjlc8nozfvc4nlbla3j0.js";
const SUPPORT_CHAT_QUERY = "(min-width: 900px)";
const SUPPORT_CHAT_PATHS = new Set([
  "/",
  "/dashboard",
  "/edit-profile",
  "/calculation",
  "/paper-instructions",
  "/additional-services",
  "/my-orders",
  "/pending-orders",
  "/in-progress",
  "/cancelled-orders",
  "/raise-complaint",
  "/my-wallet",
  "/referrals",
  "/messages",
]);
let tidioLoadPromise;
let tidioConfigured = false;

const normalizePathname = (pathname) => (
  pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname
);

const hideSupportChat = () => {
  if (!window.tidioChatApi) return;
  window.tidioChatApi.close();
  window.tidioChatApi.hide();
};

const configureTidioForButtonOnly = () => {
  if (!window.tidioChatApi || tidioConfigured) return;

  tidioConfigured = true;
  window.tidioChatApi.hide();
  window.tidioChatApi.on("close", () => {
    window.tidioChatApi.hide();
  });
};

const openSupportChat = () => {
  if (!window.tidioChatApi) return;
  window.tidioChatApi.show();
  window.tidioChatApi.open();
};

const loadTidio = () => {
  if (window.tidioChatApi) return Promise.resolve();
  if (tidioLoadPromise) return tidioLoadPromise;

  tidioLoadPromise = new Promise((resolve, reject) => {
    let script;
    const handleFailure = () => {
      clearTimeout(loadTimeout);
      document.removeEventListener("tidioChat-ready", handleReady);
      script?.removeEventListener("error", handleFailure);
      script?.remove();
      tidioLoadPromise = undefined;
      reject(new Error("Support chat failed to load"));
    };
    const handleReady = () => {
      clearTimeout(loadTimeout);
      document.removeEventListener("tidioChat-ready", handleReady);
      script?.removeEventListener("error", handleFailure);
      resolve();
    };
    const loadTimeout = setTimeout(handleFailure, 15000);

    document.addEventListener("tidioChat-ready", handleReady);

    script = document.getElementById(TIDIO_SCRIPT_ID);
    if (!script) {
      script = document.createElement("script");
      script.id = TIDIO_SCRIPT_ID;
      script.src = TIDIO_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }

    script.addEventListener("error", handleFailure, { once: true });
  });

  return tidioLoadPromise;
};

const SupportChatButton = () => {
  const { pathname } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isAllowedPath = SUPPORT_CHAT_PATHS.has(normalizePathname(pathname));

  useEffect(() => {
    const desktopQuery = window.matchMedia(SUPPORT_CHAT_QUERY);
    const enforceVisibility = () => {
      if (!desktopQuery.matches || !isAllowedPath) {
        hideSupportChat();
      } else if (window.tidioChatApi) {
        window.tidioChatApi.hide();
      }
    };

    enforceVisibility();
    desktopQuery.addEventListener("change", enforceVisibility);

    return () => {
      desktopQuery.removeEventListener("change", enforceVisibility);
      hideSupportChat();
    };
  }, [isAllowedPath]);

  if (!isAllowedPath) return null;

  const handleOpenChat = async () => {
    if (!window.matchMedia(SUPPORT_CHAT_QUERY).matches || isLoading) return;

    setIsLoading(true);

    try {
      await loadTidio();
      configureTidioForButtonOnly();
      if (
        window.matchMedia(SUPPORT_CHAT_QUERY).matches &&
        SUPPORT_CHAT_PATHS.has(normalizePathname(window.location.pathname))
      ) {
        openSupportChat();
      } else {
        hideSupportChat();
      }
    } catch (error) {
      showToast("error", "Support chat could not be loaded. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleOpenChat}
      disabled={isLoading}
      className="hidden min-[900px]:inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-shades px-4 font-inter text-sm font-semibold text-white transition hover:bg-primary disabled:cursor-wait disabled:opacity-70"
      aria-label="Chat with Support"
    >
      <HiOutlineChatBubbleLeftRight className="h-5 w-5" aria-hidden="true" />
      <span>{isLoading ? "Loading chat..." : "Chat with Support"}</span>
    </button>
  );
};

export default SupportChatButton;
